"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Eye, 
  Trash2, 
  Users as UsersIcon,
  Phone,
  Mail,
  Calendar,
  User,
  Save,
  Edit,
  Shield} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post } from "../utils/service";
import Drawer from "@/components/ui/Drawer";

interface UserType {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  phone: string;
  preferred_currency: string;
  is_active: boolean;
  created_at: string;
}

interface UsersResponse {
  success: boolean;
  message: string;
  data: UserType[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const ROLES = [
  { value: "admin", label: "Admin", color: "bg-red-100 text-red-800" },
  { value: "manager", label: "Manager", color: "bg-purple-100 text-purple-800" },
  { value: "user", label: "Normal user", color: "bg-blue-100 text-blue-800" },
];

const CURRENCIES = ["UGX", "USD", "EUR", "GBP"];

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  position?: "bottom" | "right";
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        heightPercentage={80}
        showCloseButton={false}
      >
        <div className="h-full flex flex-col bg-black text-white">
          {children}
        </div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ 
              type: 'spring', 
              damping: 30, 
              stiffness: 300 
            }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const AddUserForm = ({ 
  isOpen, 
  onClose,
  onSuccess 
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    role: "user",
    preferred_currency: "UGX"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await post("/users", formData);
      if (response.success) {
        onSuccess();
        onClose();
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          phone: "",
          role: "user",
          preferred_currency: "UGX"
        });
      }
    } catch (error) {
      console.error("Error creating user:", error);
      notify("Error creating user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Add New User</h2>
          <p className="text-xs text-gray-400 mt-1">
            They'll receive an email invitation to set their own password.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">First Name *</label>
            <input
              type="text"
              required
              value={formData.first_name}
              onChange={(e) => handleChange("first_name", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Last Name *</label>
            <input
              type="text"
              required
              value={formData.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Phone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            placeholder="+256700000000"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Role</label>
            <select
              value={formData.role}
              onChange={(e) => handleChange("role", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Currency</label>
            <select
              value={formData.preferred_currency}
              onChange={(e) => handleChange("preferred_currency", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {CURRENCIES.map(currency => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.first_name || !formData.last_name || !formData.email || !formData.phone}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending invitation...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Create & Invite User
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const EditUserForm = ({ 
  isOpen, 
  onClose,
  onSuccess,
  user
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: UserType | null;
}) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    preferred_currency: "UGX"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        preferred_currency: user.preferred_currency
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);

    try {
      const response = await put(`/users/${user.id}`, formData);
      if (response.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error updating user:", error);
      notify("Error updating user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!user) return null;

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit User</h2>
          <p className="text-xs text-gray-400 mt-1">Update user details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">First Name *</label>
            <input
              type="text"
              required
              value={formData.first_name}
              onChange={(e) => handleChange("first_name", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Last Name *</label>
            <input
              type="text"
              required
              value={formData.last_name}
              onChange={(e) => handleChange("last_name", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Email *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Phone *</label>
          <input
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Preferred Currency</label>
          <select
            value={formData.preferred_currency}
            onChange={(e) => handleChange("preferred_currency", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {CURRENCIES.map(currency => (
              <option key={currency} value={currency}>
                {currency}
              </option>
            ))}
          </select>
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Update User
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const UserDrawer = ({ 
  user, 
  isOpen, 
  onClose,
  onDelete,
  onEdit,
  onChangeRole
}: { 
  user: UserType | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (userId: string) => void;
  onEdit: () => void;
  onChangeRole: (userId: string, role: string) => void;
}) => {
  const [selectedRole, setSelectedRole] = useState("");

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
    }
  }, [user]);

  if (!user) return null;

  const getRoleInfo = (role: string) => {
    return ROLES.find(r => r.value === role) || ROLES[2];
  };

  const handleRoleChange = async () => {
    if (selectedRole !== user.role) {
      onChangeRole(user.id, selectedRole);
    }
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">User Details</h2>
          <p className="text-xs text-gray-400 mt-1">{user.first_name} {user.last_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Role</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              onBlur={handleRoleChange}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Status</label>
            <div className={`px-3 py-2 text-xs font-semibold rounded-lg ${
              user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {user.is_active ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Full Name</p>
              <p className="text-xs text-gray-400">{user.first_name} {user.last_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Email</p>
              <p className="text-xs text-gray-400">{user.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Phone className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Phone</p>
              <p className="text-xs text-gray-400">{user.phone}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Role</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleInfo(user.role).color}`}>
                {getRoleInfo(user.role).label}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Preferred Currency</p>
              <p className="text-xs text-gray-400">{user.preferred_currency}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 border-t border-gray-800">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          
          <button
            onClick={async () => {
              if (await confirmDialog("Are you sure you want to delete this user?")) {
                onDelete(user.id);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </FloatingDrawer>
  );
};

function ActionsCell({ onView, onEdit, onDelete }: { user: UserType; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <Edit className="w-3 h-3" />
            Edit User
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function UsersPage() {
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery] = useState("");
  const [roleFilter] = useState("all");
  const [statusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [showUserDrawer, setShowUserDrawer] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 5,
    total: 0,
    total_pages: 0,
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await get<UsersResponse>(
        `/users?page=${pagination.page}&page_size=${pagination.per_page}`
      );
      if (response.success) {
        setUsers(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChangeRole = async (userId: string, role: string) => {
    try {
      await put(`/users/${userId}/role`, { role });
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role } : user
      ));
      if (selectedUser?.id === userId) {
        setSelectedUser(prev => prev ? { ...prev, role } : null);
      }
      notify("User role updated successfully");
    } catch (error) {
      console.error("Error updating role:", error);
      notify("Error updating user role");
    }
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected users? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/users/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setUsers((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} users deleted successfully`);
  };

  const handleDelete = async (userId: string) => {
    if (!await confirmDialog("Are you sure you want to delete this user?")) return;

    try {
      await del(`/users/${userId}`);
      setUsers(prev => prev.filter(user => user.id !== userId));
      if (selectedUser?.id === userId) {
        setShowUserDrawer(false);
        setSelectedUser(null);
      }
      notify("User deleted successfully");
    } catch (error) {
      console.error("Error deleting user:", error);
      notify("Error deleting user");
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    const matchesStatus = statusFilter === "all" || 
      (statusFilter === "active" && user.is_active) ||
      (statusFilter === "inactive" && !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleInfo = (role: string) => {
    return ROLES.find(r => r.value === role) || ROLES[2];
  };

  const handleViewUser = (user: UserType) => {
    setSelectedUser(user);
    setShowUserDrawer(true);
  };

  const handleEditUser = (user: UserType) => {
    setSelectedUser(user);
    setShowEditUser(true);
  };

  const handleAddSuccess = () => {
    fetchUsers();
    notify("User created — an invitation email has been sent to set their password");
  };

  const handleEditSuccess = () => {
    fetchUsers();
    notify("User updated successfully");
  };

  const columns: ColumnDef<UserType>[] = [
    {
      accessorKey: "first_name",
      header: "Name",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">
            {row.original.first_name} {row.original.last_name}
          </p>
          <p className="text-xs text-gray-600">{row.original.email}</p>
        </div>
      ),
    },
    // {
    //   accessorKey: "phone",
    //   header: "Phone",
    //   cell: ({ row }) => (
    //     <p className="text-xs text-gray-900">{row.original.phone}</p>
    //   ),
    // },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleInfo(row.original.role).color}`}>
          {getRoleInfo(row.original.role).label}
        </span>
      ),
    },
    // {
    //   accessorKey: "preferred_currency",
    //   header: "Currency",
    //   cell: ({ row }) => (
    //     <p className="text-xs text-gray-900">{row.original.preferred_currency}</p>
    //   ),
    // },
    // {
    //   accessorKey: "is_active",
    //   header: "Status",
    //   cell: ({ row }) => (
    //     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    //       row.original.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    //     }`}>
    //       {row.original.is_active ? 'Active' : 'Inactive'}
    //     </span>
    //   ),
    // },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <p className="text-xs text-gray-600">
          {new Date(row.original.created_at).toLocaleDateString()}
        </p>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          user={row.original}
          onView={() => handleViewUser(row.original)}
          onEdit={() => handleEditUser(row.original)}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="User Management" description="Manage system users and access permissions">
          <button
              onClick={() => setShowAddUser(true)}
              className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200  min-w-[140px]"
            >
              Add User
            </button>
        </PageHeader>

        {/* Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredUsers} onDeleteSelected={handleBatchDelete} onAddRow={() => setShowAddUser(true)} addRowLabel="Add user">
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredUsers.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UsersIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No users found</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || roleFilter !== "all" || statusFilter !== "all" 
                      ? "Adjust your search criteria or filters to find relevant users."
                      : "Start building your team by adding the first user."
                    }
                  </p>
                  {!searchQuery && roleFilter === "all" && statusFilter === "all" && (
                    <button
                      type="button"
                      onClick={() => setShowAddUser(true)}
                      className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                    >
                      Add First User
                    </button>
                  )}
                </div>
              )}

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing{" "}
                    {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, pagination.total)}{" "}
                    of {pagination.total} users
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous page"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <UserDrawer
        user={selectedUser}
        isOpen={showUserDrawer}
        onClose={() => {
          setShowUserDrawer(false);
          setSelectedUser(null);
        }}
        onDelete={handleDelete}
        onEdit={() => {
          setShowUserDrawer(false);
          setShowEditUser(true);
        }}
        onChangeRole={handleChangeRole}
      />

      <AddUserForm
        isOpen={showAddUser}
        onClose={() => setShowAddUser(false)}
        onSuccess={handleAddSuccess}
      />

      <EditUserForm
        isOpen={showEditUser}
        onClose={() => {
          setShowEditUser(false);
          setSelectedUser(null);
        }}
        onSuccess={handleEditSuccess}
        user={selectedUser}
      />
    </div>
  );
}

export default UsersPage;